/*
 * Transcript-backed session history for students and faculty reviewers.
 *
 * Students can resume old chats from here; faculty can inspect only the
 * transcript-backed sessions they are authorized to review.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./HistoryPage.css";
import NavBar from "./Navbar";
import APIService from "../APIService";
import FacultySectionNav from "./FacultySectionNav";
import { formatLocalDateKey, formatLocalDateTime } from "../utils/datetime";
import ChatMessage from "./ChatMessage";
import { useSessionContext } from "../context/SessionContext";

function HistoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [selectedSessionData, setSelectedSessionData] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState("");

  const [courseFilter, setCourseFilter] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const isFacultyHistory = location.pathname === "/faculty/history";
  const {
    sessions,
    selectedSessionId,
    setSelectedSessionId,
    refreshSessions,
    messagesBySession,
    fetchMessages,
  } = useSessionContext();

  useEffect(() => {
    let active = true;

    async function checkAccess() {
      try {
        const data = await APIService.GetUserPrimaryAttribute();
        const isManager = Boolean(data?.faculty_access);
        const isAuthenticated = data?.primary_attribute || data?.app_role;

        // Faculty history is a stricter surface than the student self-history page.
        if (isFacultyHistory && !isManager) {
          navigate("/", { replace: true });
          return;
        }

        if (!isFacultyHistory && !isAuthenticated) {
          navigate("/", { replace: true });
          return;
        }

        if (active) {
          setAuthorized(true);
        }
      } catch (_error) {
        navigate("/", { replace: true });
      } finally {
        if (active) {
          setCheckingAuth(false);
        }
      }
    }

    checkAccess();

    return () => {
      active = false;
    };
  }, [isFacultyHistory, navigate]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    if (sessions.length > 0) {
      setLoadingSessions(false);
      return;
    }

    refreshSessions()
      .catch((err) => {
        console.error(err);
        setError("Unable to load chat sessions.");
      })
      .finally(() => {
        setLoadingSessions(false);
      });
  }, [authorized, refreshSessions, sessions.length]);

  async function loadMessages(session) {
    try {
      setSelectedSessionId(session.sessionId);
      setSelectedSessionData(session);
      setLoadingMessages(true);
      // SessionContext caches transcripts so repeated opens stay fast.
      await fetchMessages(session.sessionId);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMessages(false);
    }
  }

  const activeMessages = useMemo(() => {
    if (!selectedSessionId) {
      return [];
    }

    return messagesBySession[String(selectedSessionId)] || [];
  }, [messagesBySession, selectedSessionId]);

  useEffect(() => {
    if (!selectedSessionId) {
      return;
    }

    const activeSession = sessions.find((session) => session.sessionId === selectedSessionId);
    if (!activeSession) {
      return;
    }

    setSelectedSessionData(activeSession);
  }, [selectedSessionId, sessions]);

  const filteredSessions = useMemo(() => {
    return sessions.filter((session) => {
      const sessionDate = formatLocalDateKey(session.startTime);
      const searchText = `${session.courseCovered || ""} ${session.assignmentName || ""} ${session.preview || ""}`.toLowerCase();

      return (
        (!courseFilter || session.courseCovered === courseFilter) &&
        (!assignmentFilter || session.assignmentName === assignmentFilter) &&
        (!dateFilter || sessionDate === dateFilter) &&
        (!searchFilter || searchText.includes(searchFilter.toLowerCase()))
      );
    });
  }, [sessions, courseFilter, assignmentFilter, dateFilter, searchFilter]);

  const courses = [...new Set(sessions.map((s) => s.courseCovered).filter(Boolean))];
  const assignments = [...new Set(
    sessions
      .filter((s) => !courseFilter || s.courseCovered === courseFilter)
      .map((s) => s.assignmentName)
      .filter(Boolean)
  )];

  const sessionStats = {
    total: filteredSessions.length,
    courses: new Set(filteredSessions.map((session) => session.courseCovered).filter(Boolean)).size,
    assignments: new Set(filteredSessions.map((session) => session.assignmentName).filter(Boolean)).size,
  };

  const handleResumeChat = () => {
    if (!selectedSessionData) {
      return;
    }

    // Rehydrate the main chat workspace with the exact course/assignment context.
    navigate("/", {
      state: {
        resumeChat: {
          sessionId: selectedSessionData.sessionId,
          courseId: selectedSessionData.courseCovered,
          assignmentId: selectedSessionData.assignmentId ?? null,
          assignmentName: selectedSessionData.assignmentName || "",
        },
      },
    });
  };

  if (checkingAuth || !authorized) {
    return null;
  }

  return (
    <div className="history-container">
      <NavBar />

      <main id="main-content" className="history-content">
        <aside className="history-sidebar" aria-label="History filters and session list">
          <div className="history-hero compact">
            <div className="history-eyebrow">{isFacultyHistory ? "Dashboard / Student History" : "Student History"}</div>
            <h1>{isFacultyHistory ? "Review tutoring sessions" : "Review prior guidance"}</h1>
            <p>
              {isFacultyHistory
                ? "Filter transcript-backed sessions by course, assignment, date, or session context."
                : "Filter your prior transcript-backed tutoring sessions and resume the one you need."}
            </p>
          </div>

          {isFacultyHistory && <FacultySectionNav />}

          <div className="history-stats">
            <div className="history-stat-card">
              <span>Sessions</span>
              <strong>{sessionStats.total}</strong>
            </div>
            <div className="history-stat-card">
              <span>Courses</span>
              <strong>{sessionStats.courses}</strong>
            </div>
            <div className="history-stat-card">
              <span>Assignments</span>
              <strong>{sessionStats.assignments}</strong>
            </div>
          </div>

          <div className="history-filters">
            <div className="history-filter-field">
              <label className="history-filter-label" htmlFor="history-search">
                Search sessions
              </label>
              <input
                id="history-search"
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Search by course, assignment, or session context"
                className="history-input"
                aria-label="Search session context"
              />
            </div>

            <div className="history-filter-field">
              <label className="history-filter-label" htmlFor="history-course-filter">
                Course
              </label>
              <select
                id="history-course-filter"
                value={courseFilter}
                onChange={(e) => {
                  setCourseFilter(e.target.value);
                  setAssignmentFilter("");
                }}
                className="history-input"
                aria-label="Filter by course"
              >
                <option value="">All courses</option>
                {courses.map((course) => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            </div>

            <div className="history-filter-field">
              <label className="history-filter-label" htmlFor="history-assignment-filter">
                Assignment or topic
              </label>
              <select
                id="history-assignment-filter"
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value)}
                className="history-input"
                aria-label="Filter by assignment or topic"
              >
                <option value="">All assignments / topics</option>
                {assignments.map((assignment) => (
                  <option key={assignment} value={assignment}>{assignment}</option>
                ))}
              </select>
            </div>

            <div className="history-filter-field">
              <label className="history-filter-label" htmlFor="history-date-filter">
                Session start date
              </label>
              <input
                id="history-date-filter"
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="history-input"
                aria-label="Filter by session start date"
              />
            </div>
          </div>

          {loadingSessions && <p className="state-text" role="status">Loading sessions...</p>}
          {error && <p className="state-text error" role="alert">{error}</p>}
          {!loadingSessions && filteredSessions.length === 0 && !error && (
            <p className="state-text">
              {isFacultyHistory ? "No transcript-backed sessions matched these filters." : "No transcript-backed chat sessions matched these filters yet."}
            </p>
          )}

          <div className="session-list" role="list" aria-label="Saved chat sessions">
            {filteredSessions.map((session) => {
              const formattedDate = formatLocalDateTime(session.startTime);

              return (
                <button
                  key={session.sessionId}
                  type="button"
                  className={`session-item ${
                    selectedSessionId === session.sessionId ? "active" : ""
                  }`}
                  onClick={() => loadMessages(session)}
                  aria-pressed={selectedSessionId === session.sessionId}
                  aria-label={`Open history session for ${session.courseCovered || "general chat"} ${session.assignmentName ? `assignment ${session.assignmentName}` : "course-wide context"} started ${formattedDate}`}
                >
                  <div className="session-title-row">
                    <div className="session-title">{session.courseCovered || "General Chat"}</div>
                    <div className="session-date">{formattedDate}</div>
                  </div>
                  <div className="session-context">
                    {session.assignmentName || "Course-wide context"}
                  </div>
                  <div className="session-preview">
                    {session.preview || "No preview available yet."}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="history-transcript" aria-label="Transcript viewer">
          <div className="history-transcript-topbar">
            <div>
              <div className="history-eyebrow">Transcript</div>
              <h2>{selectedSessionData?.courseCovered || "Choose a session"}</h2>
              <p>
                {selectedSessionData
                  ? `${selectedSessionData.assignmentName || "Course-wide context"}`
                  : "Select a session to review the prior tutoring exchange."}
              </p>
            </div>
            <button
              className="resume-chat-button"
              onClick={handleResumeChat}
              disabled={!selectedSessionData}
            >
              Resume In Chat
            </button>
          </div>

          {!selectedSessionId && !loadingSessions && (
            <div className="empty-transcript modern">
              <div className="empty-transcript-card">
                <h3>Select a session to review transcript.</h3>
                <p>Use the filters on the left to narrow by course, assignment, or date.</p>
              </div>
            </div>
          )}

          {loadingMessages && (
            <div className="state-text" role="status">Loading messages...</div>
          )}

          {selectedSessionId && !loadingMessages && activeMessages.length === 0 && (
            <div className="state-text">
              No messages in this session.
            </div>
          )}

          <div className="messages" role="log" aria-live="polite" aria-label="History transcript messages">
            {activeMessages.map((msg, index) => (
              <ChatMessage
                key={`${selectedSessionId}-${index}`}
                role={msg.type === "user" ? "user" : "assistant"}
                content={msg.text}
                label={msg.type === "user" ? "You" : "AI Tutor"}
              />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default HistoryPage;
